import { TFunction } from 'i18next';
import { UsageWithSetup } from './leakageProductSetups';
import { LeakageQtyFormat, LEAKAGE_QTY_STD } from './leakagePoolDisplay';
import {
  LeakageCsvEntry,
  escapeCell,
  leakageCsvColIndex,
  leakageCsvHeaders,
  memberCells,
  money,
  noDash,
  qtyHeader,
  rowCells,
} from './leakageCsv';

/**
 * Detailed CSV export of the Leakage tab: the plain export's product rows, each followed by the
 * lines its drawer explains it with —
 *   - its ALTERNATIVE POOL members (as in the plain file),
 *   - one SECTION line per counted section: opening → counted, and the change in qty and €,
 *   - one DISH line per recipe that consumed it in the period: `per dish × sold = used`, plus
 *     that dish's share of the theoretical usage in €.
 * The same idea as the cost-price detailed export, which folds each dish's recipe underneath it.
 *
 * How to read the sheet:
 *   - `Type` is authoritative; the indentation in the product column is only for humans.
 *   - A dish line's quantities are stated in the PRODUCT's unit (the drawer's rule), so
 *     `Per dish × Sold` reconciles with `Line quantity`, and the dish lines of one product sum
 *     to its `Theoretical usage`.
 *   - `Line €` on a dish line is that dish's share of the row's theoretical usage value. On an
 *     alternative pool that share is an allocation estimate, exactly like the pool's own usage
 *     figure — the split between interchangeable products is never observed.
 *   - A section line's `Line quantity` / `Line €` are the CHANGE over the period (counted −
 *     opening), not a leakage: leakage stays on the product row, where it is computed.
 *
 * Every number arrives already computed by `inventoryVariance.ts` (the report the tab renders)
 * and `buildPerDishUsage` — nothing is re-derived or re-filtered here.
 */

/** A product row plus the per-dish usage behind its theoretical-usage figure. */
export interface LeakageDetailedCsvEntry extends LeakageCsvEntry {
  /**
   * Per-dish theoretical usage for this row's period, biggest first — `buildPerDishUsage`'s
   * output, enriched with the setup facts the drawer badges. Empty when the row has no costed
   * recipe, or when its units could not be reconciled (the engine drops usage rather than
   * printing a number in the wrong space).
   */
  dishes: UsageWithSetup[];
}

/** Columns the detail lines add after the plain layout. */
const DETAIL_COLUMNS = 4;

function detailHeaders(t: TFunction, includeGroup: boolean, format: LeakageQtyFormat): string[] {
  return [
    t('inventory.leakage.csv.colType'),
    ...leakageCsvHeaders(t, includeGroup, format),
    qtyHeader(t, format, t('inventory.leakage.csv.colPerDish')),
    t('inventory.leakage.csv.colSold'),
    qtyHeader(t, format, t('inventory.leakage.csv.colLineQty')),
    t('inventory.leakage.csv.colLineValue'),
  ];
}

/** How a dish line reached this product, when it did not come straight off the dish. */
function dishMarker(t: TFunction, usage: UsageWithSetup): string {
  if (usage.viaSubrecipe) return ` (${t('inventory.leakageDrawer.viaSubrecipe', { name: usage.viaSubrecipe })})`;
  // Only reachable through a setup that sold nothing: `buildPerDishUsage` walks primaries only.
  if (usage.viaAlternative) return ` (${t('inventory.leakage.csv.viaAlternative')})`;
  return '';
}

export function buildLeakageDetailedCsv(
  t: TFunction,
  entries: LeakageDetailedCsvEntry[],
  includeGroup: boolean,
  format: LeakageQtyFormat
): string {
  const plainWidth = leakageCsvHeaders(t, includeGroup, format).length;
  const width = plainWidth + DETAIL_COLUMNS;
  const iName = leakageCsvColIndex('product', includeGroup);
  const iOpening = leakageCsvColIndex('openingQty', includeGroup);
  const iCounted = leakageCsvColIndex('countedQty', includeGroup);
  const iPerDish = plainWidth;
  const iSold = plainWidth + 1;
  const iLineQty = plainWidth + 2;
  const iLineValue = plainWidth + 3;

  const blank = (): string[] => new Array(width).fill('');
  /** A plain-export line (product / pool member) padded out to the wider layout. */
  const pad = (cells: string[]): string[] => [...cells, ...new Array(DETAIL_COLUMNS).fill('')];

  const lines = [detailHeaders(t, includeGroup, format).map(escapeCell).join(',')];
  const push = (type: string, cells: string[]) => lines.push([type, ...cells].map(escapeCell).join(','));

  for (const entry of entries) {
    const { row } = entry;
    push(t('inventory.leakage.csv.typeProduct'), pad(rowCells(t, entry, includeGroup, format)));

    for (const m of row.members ?? []) {
      push(t('inventory.leakage.csv.typeAlternative'), pad(memberCells(t, m, includeGroup, format)));
    }

    for (const s of row.sectionChanges) {
      const cells = blank();
      cells[iName] = `  - ${s.sectionName}${s.deleted ? ` ${t('inventory.leakage.deletedSection')}` : ''}`;
      // Through the row's guard: on a mixed-unit pool (or a row whose two counts share no unit
      // space) the engine leaves these at a literal 0, and printing that reads as "counted empty".
      cells[iOpening] = noDash(format.row(row, s.startQty));
      cells[iCounted] = noDash(format.row(row, s.endQty));
      cells[iLineQty] = noDash(format.row(row, s.deltaQty));
      cells[iLineValue] = money(s.deltaValue);
      push(t('inventory.leakage.csv.typeSection'), cells);
    }

    // Σ of the dish lines, which is what the row's usage was split over — not `neededQty`,
    // so the shares still add up to `neededValue` if the two ever drift apart.
    const usedBase = entry.dishes.reduce((sum, u) => sum + u.totalBase, 0);
    for (const u of entry.dishes) {
      const cells = blank();
      cells[iName] = `  - ${u.label}${dishMarker(t, u)}`;
      // DISH lines stay in STD units whatever the toggle says — the same rule the drawer's
      // "Needed quantity breakdown" follows (spec decision 6). These are recipe quantities and
      // read in the unit the recipe is written in; converting them here while the drawer does
      // not would print the same figure in two different units on one screen and one file.
      cells[iPerDish] = noDash(LEAKAGE_QTY_STD.rowPrecise(row, u.basePerUnit));
      cells[iSold] = String(u.unitsSold);
      cells[iLineQty] = noDash(LEAKAGE_QTY_STD.row(row, u.totalBase));
      cells[iLineValue] =
        row.neededValue === null || usedBase <= 0 ? '' : money((row.neededValue * u.totalBase) / usedBase);
      push(t('inventory.leakage.csv.typeDish'), cells);
    }
  }

  return lines.join('\n');
}
